/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useRef, useState } from 'react';
import ReactPlayer, { ReactPlayerProps } from 'react-player';
import {
  ProductRangeTypes,
  PinnedProductTypes,
  LiveStreamStatus,
  MessageDataSocket,
  LuckyDetail,
  Lottery,
} from '@/features/livestream/detail/const';
import { TFunction } from 'next-i18next';
import {
  useEndWatchVideoQuery,
  useFetchLuckyBuyLiveIdQuery,
  useWatchVideoQuery,
} from '../../api';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AuctionStatus,
  CommentMethod,
  EventType,
  LiveProductMethod,
  LotteryGameStatus,
  LotterySocketMethod,
  LuckyStatus,
  MessageMethodType,
  ProductStatus,
} from '@/layouts/shared';
import { Icon, Image, WaveIcon } from '@/components';
import { LuckyPartial } from './Lucky';
import { useRouter } from 'next/router';
import { useProfile } from '@/contexts/profile';
import { LotteryPartial } from '../lottery';
import AuctionCountDown from './AuctionCountDown';

type PlayerPartialProps = {
  url: string;
  liveImage?: string;
  productsRange: ProductRangeTypes[];
  pinnedProduct: PinnedProductTypes | undefined;
  liveStreamId: number;
  liveStatus: string;
  t: TFunction;
  messageData: MessageDataSocket;
  setPinnedProduct: (pinnedProduct: PinnedProductTypes | any) => void;
  isAuth: boolean;
  sticker: string | null;
  showLotteryBtn: () => void;
  hideLotteryBtn: () => void;
  lotteryGame: Lottery | null;
  luckyDetail: LuckyDetail;
};

const PlayerPartial = ({
  url,
  productsRange,
  pinnedProduct,
  setPinnedProduct,
  liveStreamId,
  liveStatus,
  t,
  lotteryGame,
  isAuth,
  sticker,
  messageData,
  showLotteryBtn,
  hideLotteryBtn,
  luckyDetail,
}: PlayerPartialProps) => {
  const router = useRouter();
  const profile: any = useProfile();
  const { mutate: mutateWatchVideo } = useWatchVideoQuery();
  const { mutate: mutateEndWatchVideo } = useEndWatchVideoQuery();
  const [isVideoError, setVideoError] = useState(false);
  const [videoHeight, setVideoHeight] = useState<string>('');
  const [lucky, setLucky] = useState<LuckyDetail | null | undefined>(null);

  const [gameId, setGameId] = useState<string | null>(null);
  const [gameStatus, setGameStatus] = useState<string>(
    lotteryGame?.status || LotteryGameStatus.Ended
  );
  const refDiv = useRef<any>(null);
  const playerRef = useRef<any>(null);
  const gameRef = useRef<any>(null);
  const start = router.query.start ? parseInt(router.query.start + '') : 0;
  console.log(luckyDetail);
  useEffect(() => {
    setGameId(lotteryGame?.id || null);
  }, [lotteryGame?.id]);
  useEffect(() => {
    setLucky(luckyDetail);
  }, [liveStreamId, luckyDetail]);

  useEffect(() => {
    if (isAuth) mutateWatchVideo({});
  }, [isAuth, mutateWatchVideo]);

  const { refetch: refetchLucky } = useFetchLuckyBuyLiveIdQuery(liveStreamId, {
    enabled: false,
    retry: false,
    onSuccess: ({ data }) => {
      if (data.status === LuckyStatus.PROCESSING) setLucky(data);
    },
    onError: () => {
      setLucky(null);
    },
  });

  useEffect(() => {
    const handleTabClose = (event: any) => {
      event.preventDefault();
      if (isAuth)
        mutateEndWatchVideo({
          enable: false,
        });
    };
    window.addEventListener('beforeunload', handleTabClose);
    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [isAuth, mutateEndWatchVideo]);

  const onVideoPlaying = ({
    playedSeconds: currentPlayedSecond,
    played,
  }: ReactPlayerProps) => {
    if (liveStatus !== LiveStreamStatus.LIVE_NOW) {
      const currentProductRange = productsRange.find(({ range }) => {
        const [rangeStart, rangeEnd] = range;

        return (
          rangeStart <= currentPlayedSecond && rangeEnd >= currentPlayedSecond
        );
      });
      if (currentProductRange) {
        const { product } = currentProductRange;
        const { product_id: currentProductId } = product;
        if (
          currentProductId !== pinnedProduct?.product_id &&
          product.products.status !== ProductStatus.Hide
        ) {
          setPinnedProduct(product);
        }
      }
    }
  };

  useEffect(() => {
    setVideoError(false);
  }, [url]);
  useEffect(() => {
    if (refDiv) {
      setVideoHeight(refDiv?.current?.clientHeight);
    }
  }, []);
  const handleOnError = (data: any) => {
    setVideoError(data.isTrusted);
  };
  let playerConfig: any = {
    attributes: {
      height: videoHeight,
    },
  };
  const endGame = () => {
    if (gameRef.current) clearTimeout(gameRef.current);
    gameRef.current = setTimeout(() => {
      setGameId(null);
      hideLotteryBtn();
      setGameStatus(LotteryGameStatus.Ended);
    }, 10000);
  };
  useEffect(() => {
    if (messageData?.method === CommentMethod.ProcessingLucky) {
      if (!messageData.data) return;
      setLucky(messageData.data);
    }
    if (messageData?.method === CommentMethod.FinishLucky) setLucky(null);
    if (messageData?.method === LotterySocketMethod.OpenLottery) {
      setGameStatus(LotteryGameStatus.Pending);
      setGameId(messageData.data?.lottery_game_id || '');
    }
    if (messageData?.method === LotterySocketMethod.EndLottery) {
      endGame();
    }
    if (messageData?.method === LiveProductMethod.PinProduct) {
      const p = messageData.data.live_stream_product;
      if (p && p.product_id !== pinnedProduct?.product_id) {
        setPinnedProduct(p);
      }
    }
    if (
      messageData?.method === LiveProductMethod.AuctionStarted ||
      messageData?.method === LiveProductMethod.AuctionEnded
    ) {
      if (!messageData.data) return;

      setPinnedProduct((prev: any) => {
        return {
          ...prev,
          products: {
            ...prev.products,
            auction: {
              ...prev.products.auction,
              ended_time_at: messageData.data.ended_time_at,
              started_at: messageData.data.started_at,
              status: messageData.data.status,
            },
          },
        };
      });
    }
    if (messageData?.method === LiveProductMethod.AuctionOffer) {
      setPinnedProduct((prev: any) => {
        if (!messageData.data) return;
        return {
          ...prev,
          products: {
            ...prev.products,
            auction: {
              ...prev.products.auction,
              sell_price: messageData.data.sell_price || 0,
            },
          },
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageData]);

  const renderLucky = (type: string | undefined) => {
    if (!type) return '';
    if (type == EventType.Gift) return 'Quà tặng';
    if (type == EventType.Wishes) return 'Lời chúc';
    if (type == EventType.Voucher) return 'Voucher';
  };
  const checkMyAccount = (
    type: string | undefined,
    userId: number | undefined
  ) => {
    if (type === EventType.Wishes && userId === profile?.id) return false;
    return true;
  };
  return (
    <>
      <div
        className="tw-h-full tw-w-full tw-relative"
        style={{ minHeight: '450px' }}
        ref={refDiv}
      >
        {isVideoError && (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-full">
            {t('detail.error')}
          </div>
        )}
        {!isVideoError && videoHeight !== '' && (
          <ReactPlayer
            url={url}
            width="100%"
            height="100%"
            className="react-player"
            style={{ textAlign: 'center', backgroundColor: 'black' }}
            ref={playerRef}
            autoPlay
            muted
            playing
            controls={true}
            onReady={() => (playerRef.current as any)?.seekTo(start, 'seconds')}
            onProgress={onVideoPlaying}
            onError={handleOnError}
            config={playerConfig}
            onStart={() => console.log('start')}
            onEnded={() => console.log('end')}
          />
        )}
        <AnimatePresence>
          {lucky && (
            <motion.div
              className="tw-absolute tw-right-4 tw-bottom-20 tw-z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <LuckyPartial lucky={lucky} callRefetch={() => refetchLucky()} />
            </motion.div>
          )}
        </AnimatePresence>
        {gameId && gameStatus !== LotteryGameStatus.Ended && (
          <div className="tw-absolute tw-left-2 tw-top-4 md:tw-left-6 md:tw-top-16">
            <LotteryPartial
              isAuth={isAuth}
              t={t}
              gameId={gameId}
              liveStreamId={liveStreamId}
              messageData={messageData}
              showLotteryBtn={showLotteryBtn}
              gameStatus={gameStatus}
            />
          </div>
        )}
        {pinnedProduct?.products.auction?.status === AuctionStatus.Started && (
          <div className="tw-absolute tw-right-2 tw-top-5">
            <AuctionCountDown
              endTime={pinnedProduct?.products.auction.ended_time_at}
            />
          </div>
        )}
        <div className="bubbling-heart tw-w-full tw-h-full tw-z-20">
          <AnimatePresence>
            {sticker &&
              [1, 2, 3, 4].map((i) => {
                return (
                  <motion.div
                    key={i}
                    className="tw-w-7 tw-h-7 tw-rounded-full"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <img
                      src={sticker}
                      className="tw-w-full tw-h-full"
                      alt={sticker}
                    />
                  </motion.div>
                );
              })}
          </AnimatePresence>
        </div>
        <div className="tw-absolute tw-left-2 tw-bottom-3 tw-gap-y-2 tw-flex tw-flex-col">
          {!!messageData &&
            messageData.method ===
              LotterySocketMethod.AdminPickTicketLottery && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="tw-bg-black tw-bg-opacity-30 tw-rounded-full tw-px-4 tw-py-1 tw-text-white tw-flex tw-items-center tw-text-base"
                >
                  <span>Kết quả xổ số: {messageData.data.number}</span>
                  <Image
                    src="/confetti-flat.gif"
                    alt="confetti"
                    className="tw-w-6 tw-h-6 tw-ml-1"
                  ></Image>
                  <Image
                    src="/confetti-flat.gif"
                    alt="confetti"
                    className="tw-w-6 tw-h-6 tw-ml-1"
                  ></Image>
                  <Image
                    src="/confetti-flat.gif"
                    alt="confetti"
                    className="tw-w-6 tw-h-6 tw-ml-1"
                  ></Image>
                </motion.div>
              </AnimatePresence>
            )}
          {!!messageData &&
            messageData.method === CommentMethod.OpenLuckyBox &&
            checkMyAccount(
              messageData?.data.type,
              messageData.data.users?.id
            ) && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="tw-bg-black tw-bg-opacity-30 tw-rounded-full tw-px-4 tw-py-1 tw-text-white tw-flex tw-items-center tw-text-base"
                >
                  <div>
                    {messageData?.data?.users?.fullname} đã mở được&nbsp;
                    <span>{renderLucky(messageData.data.type)}</span>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          {!!messageData &&
            messageData.method === MessageMethodType.Sticker && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="tw-bg-black tw-bg-opacity-30 tw-rounded-full tw-px-4 tw-py-1 tw-text-white tw-flex tw-items-center tw-text-base"
                >
                  <span>{messageData.data.fullname} đã tặng &nbsp;</span>
                  <Image
                    alt={messageData?.data?.sticker_icon}
                    src={messageData?.data?.sticker_icon}
                    className="tw-w-6 tw-h-6 tw-rounded-full"
                    objectFit="cover"
                  ></Image>
                </motion.div>
              </AnimatePresence>
            )}
          {!!messageData &&
            messageData.method === MessageMethodType.JoinLivestream && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="tw-px-4 tw-py-1 tw-text-white tw-flex tw-items-center tw-text-base"
                >
                  <span>{messageData.data.fullname} đã tham gia &nbsp;</span>
                  <Icon
                    className="tw-flex tw-items-center tw-justify-center"
                    icon={<WaveIcon />}
                  />
                </motion.div>
              </AnimatePresence>
            )}
          {!!messageData &&
            messageData.method === MessageMethodType.GiftAtd && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="tw-px-4 tw-py-1 tw-text-white tw-flex tw-items-center tw-text-base"
                >
                  <span>{messageData.data.users?.fullname} đã tặng</span>
                  <Image
                    src="/diamond-kam.png"
                    alt="diamond"
                    className="tw-w-4 tw-h-4 tw-mx-2"
                  />
                  <span className="tw-text-white tw-font-bold">
                    {messageData.data.amount} ATD
                  </span>
                </motion.div>
              </AnimatePresence>
            )}
        </div>
        {liveStatus === LiveStreamStatus.LIVE_NOW && (
          <div className="tw-absolute tw-left-6 tw-top-[60px] tw-flex tw-items-center tw-px-4 tw-bg-red-600 tw-text-white tw-font-xs tw-rounded tw-mr-2">
            <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-white tw-mr-1 tw-block"></span>
            Live
          </div>
        )}
      </div>
    </>
  );
};

PlayerPartial.defaultProps = {
  products: [],
  prevPlayedSeconds: 0,
};

export default PlayerPartial;
